/**
 * ResetPasswordView — Set new password using reset token from email.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { createPasswordStrength } from '../components/PasswordStrength';
import { validatePassword, validatePasswordMatch } from '../validators';
import { resetPassword } from '../AuthService';

export async function mountResetPasswordView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: {
    onNavigate: (view: string, params?: Record<string, string>) => void;
    token?: string;
  },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_reset_password_title'));
  layout.setSubtitle(t('auth_reset_password_subtitle'));

  const token = opts.token || '';

  if (!token) {
    layout.showError(t('auth_error_invalid_reset_token'));
    layout.card.innerHTML = `
      <div style="text-align:center;padding:16px 0;">
        <button class="auth-btn auth-btn-primary" id="auth-reset-back">${t('auth_back_to_login')}</button>
      </div>
    `;
    layout.card.querySelector('#auth-reset-back')?.addEventListener('click', () => opts.onNavigate('login'));
    container.innerHTML = '';
    container.appendChild(layout.wrapper);
    return { unmount() { container.innerHTML = ''; } };
  }

  let loading = false;
  const form = document.createElement('form');
  form.setAttribute('novalidate', '');

  // New password
  const pwField = _createPasswordFieldWithStrength('auth-reset-pw', t('auth_new_password_label'), 'new-password', t);
  form.appendChild(pwField.wrapper);

  // Confirm password
  const confirmField = _createField('confirm', t('auth_confirm_password_label'), 'password', 'new-password');
  form.appendChild(confirmField.wrapper);

  // Submit
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_reset_password_button')}</span>`;
  form.appendChild(submitBtn);

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;
    layout.clearMessages();

    const pw = pwField.input.value;
    const confirm = confirmField.input.value;

    const pwResult = validatePassword(pw);
    if (!pwResult.valid) {
      layout.showError(t(pwResult.errors[0]));
      return;
    }

    const matchResult = validatePasswordMatch(pw, confirm);
    if (!matchResult.valid) {
      layout.showError(t(matchResult.errors[0]));
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    const result = await resetPassword(token, pw);

    if (result.ok) {
      layout.showSuccess(t('auth_password_reset_success'));
      setTimeout(() => opts.onNavigate('login'), 2000);
    } else {
      let msg = t(`auth_error_${result.error.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = result.error.error;
      layout.showError(msg);
    }

    submitBtn.disabled = false;
    submitBtn.innerHTML = `<span>${t('auth_reset_password_button')}</span>`;
    loading = false;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return { unmount() { container.innerHTML = ''; } };
}

function _createField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';
  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', `auth-reset-${name}`);
  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = type;
  input.id = `auth-reset-${name}`;
  input.name = name;
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);
  wrapper.append(labelEl, input);
  return { wrapper, input };
}

function _createPasswordFieldWithStrength(
  id: string,
  label: string,
  autocomplete: string,
  t: (key: string) => string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';
  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', id);
  const inputWrap = document.createElement('div');
  inputWrap.className = 'auth-input-wrap';
  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = 'password';
  input.id = id;
  input.name = 'password';
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);
  inputWrap.appendChild(input);
  const strength = createPasswordStrength(t);
  input.addEventListener('input', () => strength.update(input.value));
  wrapper.append(labelEl, inputWrap, strength.container);
  return { wrapper, input };
}
